import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type TAppName = 'oi-app' | 'plani8' | 'ui' | 'ba-app' | 'input-app' | 'mm' | 'organizi8';

type TTranslations = Record<string, unknown>;

const COMMON_TRANSLATION_SOURCES = ['./assets/ui-i18n/'];

const SOURCES: Record<TAppName, string[]> = {
  ui: [...COMMON_TRANSLATION_SOURCES],
  'oi-app': [...COMMON_TRANSLATION_SOURCES, './assets/i18n/'],
  plani8: [...COMMON_TRANSLATION_SOURCES],
  'ba-app': [...COMMON_TRANSLATION_SOURCES, './assets/i18n/'],
  'input-app': [...COMMON_TRANSLATION_SOURCES, './assets/i18n/'],
  mm: [...COMMON_TRANSLATION_SOURCES, './assets/i18n/common/', './assets/i18n/mobile/', './assets/i18n/web/'],
  organizi8: [...COMMON_TRANSLATION_SOURCES, './assets/i18n/'],
};

function isTranslationGroup(value: unknown): value is TTranslations {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Merges translation files recursively so that two sources declaring the same top level namespace
 * contribute their keys instead of one replacing the other.
 * On a leaf conflict the value from `source` wins, so later entries in SOURCES override earlier ones.
 */
function mergeTranslations(target: TTranslations, source: TTranslations): TTranslations {
  const merged: TTranslations = { ...target };
  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = merged[key];
    merged[key] =
      isTranslationGroup(sourceValue) && isTranslationGroup(targetValue)
        ? mergeTranslations(targetValue, sourceValue)
        : sourceValue;
  });
  return merged;
}

export class TranslationLoaderService implements TranslateLoader {
  constructor(private http: HttpClient, private appName: TAppName) {}

  getTranslation(lang: string): Observable<TTranslations> {
    const requests = SOURCES[this.appName].map((source) => {
      const url = `${source}${lang}.json`;
      return this.http.get<TTranslations>(url).pipe(
        catchError((error) => {
          console.log(`[ERROR]: Could not load translation files. ${url}`, error);
          return of({} as TTranslations);
        }),
      );
    });

    // forkJoin emits in `SOURCES` order regardless of which request responds first
    // so app specific files always override the shared ones on a key conflict
    // instead of it depending on the race
    return forkJoin(requests).pipe(map((translations) => translations.reduce(mergeTranslations, {} as TTranslations)));
  }
}

@Injectable({
  providedIn: 'root',
})
export class TranslationLoaderFactory {
  constructor(private http: HttpClient) {}

  createForApp(appName: TAppName): TranslationLoaderService {
    return new TranslationLoaderService(this.http, appName);
  }
}
